//library
import React, { useState, useEffect } from "react";
import {
	Card,
	Table,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TableFooter,
	TablePagination,
	TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Link, useNavigate } from "react-router-dom";

//code
import useListApplication from "../../services/api/useListApplication";
import useStyles from "../../components/StatusViewerTable/styles/styles";
import { TraxxButton, TraxxInput } from "../../components";
import ApplicationTable from "../../components/StatusViewerTable/layout/ApplicationTable";
import ApplicationTableFooterActions from "../../components/StatusViewerTable/layout/ApplicationTableFooter";
import ManualOverrideDialog from "../../components/ManualOverrideDialog/Dialog";
import ReferralDialog from "../../components/StatusViewerTable/helpers/ReferralDialog/Dialog";

/**
 * The Merchant Applicaton Status Viewer App
 * @returns - Search bar and table filled with applications
 */
const Applications = () => {
	const classes = useStyles();
	const navigate = useNavigate();

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [openManualOverride, setOpenManualOverride] = useState(false);
	const [openReferralDialog, setOpenReferralDialog] = useState(false);

	const { enqueueSnackbar } = useSnackbar();
	const [applicationId, setApplicationId] = useState("");
	const [applicantData, setApplicantData] = useState("");

	const {
		loadApplications,
		searchApplications,
		applications,
		count,
		monthlyApplications,
		yearlyApplications,
		loading,
		handleSearch,
		searchTerm,
		searchStartDate,
		setSearchStartDate,
		searchEndDate,
		setSearchEndDate,
	} = useListApplication();
	/**
	 * Checks if window is reloading and redirect to the login page if reloading.
	 */
	useEffect(() => {
		window.addEventListener("load", (e) => {
			e.preventDefault();
			if (e) {
				returnLogin();
				navigate("/statusViewer/");
			}
		});
		if (loading) loadApplications();
	});

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
		searchApplications((newPage + 1) * rowsPerPage, newPage * rowsPerPage);
	};

	const handleChangeRowsPerPage = (event) => {
		const newRows = parseInt(event.target.value, 10);
		console.log(newRows);
		setRowsPerPage(newRows);
		setPage(0);
		searchApplications(newRows, 0);
	};

	/**
	 * Clear user_email and access_token from local storage when exiting page via logout or reloading
	 */
	const returnLogin = () => {
		localStorage.setItem("user_email", "");
		localStorage.setItem("access_token", "");
	};

	const openManualOverrideBox = (applicationId) => {
		setApplicationId(applicationId);
		setOpenManualOverride(true);
	};

	const sendManualOverrideClose = () => {
		setOpenManualOverride(false);
	};
	const openReferralDialogBox = (applicantData) => {
		setApplicantData(applicantData);
		setOpenReferralDialog(true);
	};

	const sendReferralDialogBox = () => {
		setOpenReferralDialog(false);
	};

	return (
		<div className={classes.content}>
			<h2 className={classes.headings}>Merchant Application Status Viewer</h2>
			<div className={classes.logoutRow}>
				<Link
					to="/statusViewer/"
					onClick={returnLogin}
					className={classes.logout}
				>
					Logout
				</Link>
			</div>
			{loading ? (
				<Card className={classes.card}>
					<h4 className={classes.headings}>Processing request</h4>
					<TraxxButton onSubmit={() => window.location.reload(false)}>
						Reload
					</TraxxButton>
				</Card>
			) : (
				<Card className={classes.card}>
					<TableContainer component={Paper}>
						<Table aria-label="simple table" stickyHeader={true}>
							<TableHead>
								<TableRow>
									<TableCell>
										YTD Applications: <b>{yearlyApplications}</b>
									</TableCell>
									<TableCell>
										MTD Applications: <b>{monthlyApplications}</b>
									</TableCell>
									<TableCell colSpan={4}>
										<TraxxInput
											label="Search for an Application"
											input={searchTerm}
											onChange={handleSearch}
											value={searchTerm}
											placeholder=""
											autoComplete="off"
										/>
									</TableCell>
									<TableCell colSpan={4}>
										<LocalizationProvider dateAdapter={AdapterMoment}>
											<DatePicker
												disableFuture
												label="Search by Date from"
												value={searchStartDate}
												onChange={(newValue) => {
													setSearchStartDate(newValue);
												}}
												renderInput={(params) => <TextField {...params} />}
											/>
											&nbsp;
											<DatePicker
												disableFuture
												label="Till"
												value={searchEndDate}
												onChange={(newValue) => {
													searchApplications(
														undefined,
														undefined,
														searchTerm,
														searchStartDate.unix() +
															"," +
															newValue.endOf("day").unix()
													);
													setSearchEndDate(newValue);
												}}
												renderInput={(params) => <TextField {...params} />}
											/>
										</LocalizationProvider>
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Application ID</TableCell>
									<TableCell>Application Date</TableCell>
									<TableCell>Company Name</TableCell>
									<TableCell align="center">Enquiry Status</TableCell>
									<TableCell align="center">NDA Signed</TableCell>
									<TableCell align="center">Application Form</TableCell>
									<TableCell align="center">Documents Upload</TableCell>
									<TableCell align="center">Current Status</TableCell>
									<TableCell>Referred By:</TableCell>
									<TableCell>Remarks</TableCell>
								</TableRow>
							</TableHead>
							<ApplicationTable
								applications={applications}
								openManualOverrideBox={openManualOverrideBox}
								openReferralDialogBox={openReferralDialogBox}
							/>
							<TableFooter>
								<TableRow>
									<TablePagination
										rowsPerPageOptions={[10, 25, 50]}
										colSpan={9}
										count={count}
										rowsPerPage={rowsPerPage}
										page={page}
										SelectProps={{
											inputProps: {
												"aria-label": "rows per page",
											},
											native: true,
										}}
										onPageChange={handleChangePage}
										onRowsPerPageChange={handleChangeRowsPerPage}
										ActionsComponent={ApplicationTableFooterActions}
									/>
								</TableRow>
							</TableFooter>
						</Table>
					</TableContainer>
					<ManualOverrideDialog
						enqueueSnackbar={enqueueSnackbar}
						loadApplications={loadApplications}
						openManualOverride={openManualOverride}
						sendManualOverrideClose={sendManualOverrideClose}
						applicationId={applicationId}
					/>
					<ReferralDialog
						enqueueSnackbar={enqueueSnackbar}
						openReferralDialog={openReferralDialog}
						sendReferralDialogBox={sendReferralDialogBox}
						applicantData={applicantData}
					/>
				</Card>
			)}
		</div>
	);
};
export default Applications;
